import { baseApi } from './baseApi';
import {
  Customer,
  CustomerAddress,
  CustomerSearchResult,
  CreateCustomerDto,
  UpdateCustomerDto,
  CreateCustomerAddressDto,
  UpdateCustomerAddressDto,
  LinkClienteFiscalDto,
} from '../types/customer';

export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Search customers by phone
    searchCustomerByPhone: builder.query<CustomerSearchResult[], string>({
      query: (phone) => `/customers/search?phone=${encodeURIComponent(phone)}`,
      providesTags: ['Customer'],
    }),

    // Search customers by name or phone (general search)
    searchCustomers: builder.query<CustomerSearchResult[], string>({
      query: (query) => `/customers/search?query=${encodeURIComponent(query)}`,
      providesTags: ['Customer'],
    }),

    // Get customer by ID
    getCustomer: builder.query<Customer, string>({
      query: (id) => `/customers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),

    // Get all customers (paginated)
    getCustomers: builder.query<{ items: Customer[]; total: number }, { page?: number; pageSize?: number }>({
      query: ({ page = 1, pageSize = 20 }) => `/customers?page=${page}&pageSize=${pageSize}`,
      providesTags: ['Customer'],
    }),

    // Create customer
    createCustomer: builder.mutation<Customer, CreateCustomerDto>({
      query: (data) => ({
        url: '/customers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer'],
    }),

    // Update customer
    updateCustomer: builder.mutation<Customer, { id: string; data: UpdateCustomerDto }>({
      query: ({ id, data }) => ({
        url: `/customers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Customer', id }, 'Customer'],
    }),

    // Delete customer (soft delete)
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `/customers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Customer'],
    }),

    // Get customer addresses
    getCustomerAddresses: builder.query<CustomerAddress[], string>({
      query: (customerId) => `/customers/${customerId}/addresses`,
      providesTags: (result, error, customerId) => [
        { type: 'CustomerAddress', id: customerId },
        'CustomerAddress',
      ],
    }),

    // Add address to customer
    addCustomerAddress: builder.mutation<CustomerAddress, { customerId: string; data: CreateCustomerAddressDto }>({
      query: ({ customerId, data }) => ({
        url: `/customers/${customerId}/addresses`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'Customer', id: customerId },
        { type: 'CustomerAddress', id: customerId },
        'Customer',
        'CustomerAddress',
      ],
    }),

    // Update address
    updateCustomerAddress: builder.mutation<CustomerAddress, { addressId: string; data: UpdateCustomerAddressDto }>({
      query: ({ addressId, data }) => ({
        url: `/customers/addresses/${addressId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Customer', 'CustomerAddress'],
    }),

    // Delete address
    deleteCustomerAddress: builder.mutation<void, string>({
      query: (addressId) => ({
        url: `/customers/addresses/${addressId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Customer', 'CustomerAddress'],
    }),

    // Link customer to ClienteFiscal
    linkClienteFiscal: builder.mutation<Customer, { customerId: string; data: LinkClienteFiscalDto }>({
      query: ({ customerId, data }) => ({
        url: `/customers/${customerId}/link-cliente-fiscal`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'Customer', id: customerId }, 'Customer'],
    }),

    // Unlink customer from ClienteFiscal
    unlinkClienteFiscal: builder.mutation<Customer, string>({
      query: (customerId) => ({
        url: `/customers/${customerId}/unlink-cliente-fiscal`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, customerId) => [{ type: 'Customer', id: customerId }, 'Customer'],
    }),
  }),
});

export const {
  useSearchCustomerByPhoneQuery,
  useLazySearchCustomerByPhoneQuery,
  useSearchCustomersQuery,
  useLazySearchCustomersQuery,
  useGetCustomerQuery,
  useGetCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerAddressesQuery,
  useAddCustomerAddressMutation,
  useUpdateCustomerAddressMutation,
  useDeleteCustomerAddressMutation,
  useLinkClienteFiscalMutation,
  useUnlinkClienteFiscalMutation,
} = customerApi;
